import { $el, $list, delay } from 'src/scripts/utils/utils'
import { Nullable } from 'src/scripts/types/common'
import { debounce } from 'src/scripts/utils/debounce'
import { BaseComponent } from '../base-component'
import { FloatElement } from '../float-element'

export class CollapsedTags extends BaseComponent {
  extendButton: Nullable<HTMLElement>
  tags: HTMLElement[]
  hiddenTags: FloatElement
  extendButtonWidth: number
  maxRightPoint: number
  resizeObserver: ResizeObserver

  constructor() {
    super()

    this.tags = $list('[data-tag]', this)
    this.hiddenTags =
      $el('[data-collapsed-tags]', this) ?? $el('#show_other_tags')
    this.extendButton = $el('[data-extend-button]', this)
    this.extendButtonWidth = this.extendButton?.offsetWidth ?? 0
    this.maxRightPoint =
      this.getBoundingClientRect().right - this.extendButtonWidth - 20
  }

  mountComponent(): void {
    this.resizeObserver = new ResizeObserver(this.handleContentResize)
    this.resizeObserver.observe(this)
  }

  unmountComponent(): void {
    this.resizeObserver.disconnect()
  }

  handleContentResize = debounce((): void => {
    this.setOffsets()

    this.update()
  }, 200)

  updateButtonVisible(): void {
    if (!this.extendButton) {
      return
    }

    const hiddenTags: NodeListOf<HTMLElement> =
      this.querySelectorAll('[tag-hidden]')
    const hasHiddenTags = hiddenTags.length > 0

    if (hasHiddenTags) {
      const left = hiddenTags[0].style.left
      this.extendButton.style.left = left
    }

    this.extendButton.classList.toggle('hidden', !hasHiddenTags)
    this.setAttribute('data-is-collapsed', hasHiddenTags.toString())
  }

  setOffsets(): void {
    this.tags.forEach(tag => {
      tag.style.left = `${tag.offsetLeft}px`
      tag.style.top = `${tag.offsetTop}px`
    })
  }

  updateTagsVisible(): void {
    const collapsedTagsFragment = document.createDocumentFragment()

    this.tags.forEach(tag => {
      const tagRightPosition = tag.getBoundingClientRect().right
      const isOverflowed = tagRightPosition > this.maxRightPoint

      if (isOverflowed) {
        const clone = tag.cloneNode(true) as HTMLElement

        this.setTagVisible(clone, true)

        collapsedTagsFragment.appendChild(clone)
      }

      this.setTagVisible(tag, !isOverflowed)
    })

    this.hiddenTags?.element.replaceChildren(collapsedTagsFragment)
  }

  setTagVisible(tag: HTMLElement, isVisible: boolean): void {
    tag.toggleAttribute('tag-hidden', !isVisible)
  }

  resetVisibility = () => {
    this.tags.forEach(tag => {
      this.setTagVisible(tag, false)
    })
  }

  update(): void {
    this.extendButtonWidth = this.extendButton?.offsetWidth ?? 0
    this.maxRightPoint =
      this.getBoundingClientRect().right - this.extendButtonWidth - 20

    this.updateTagsVisible()
    this.updateButtonVisible()

    this.setAttribute('visible', '')
  }
}
